/*
 * The SubscriptionTableRow component encapsulates display of a subscription in a table row.  It is intended for use
 * in the SO flows.
 * 
 * This takes the following props:
 *   subscription            - the subscription object
 *   styledTableRow          - function wrapper for emitting a styled table row
 *   styledTableCell         - function wrapper for emitting a styled table cell
 *   styledTableCellNarrow   - function wrapper for emitting a styled table cell (narrow button variant)
 *   confirmDelete           - parent function for subscription deletion (optional)
 *   showSubscriptionEdit    - parent function for subscription edit (optional)
 *   editable                - if true, show edit / delete buttons (if actions are provided)
 *   showModel               - if true, show the model
 *   showTerms               - if true, show the terms
 *   soContext               - resellerSetup, customerView, or orderHistory
 */

import React from 'react';
import PropTypes from 'prop-types';
import { red } from '@mui/material/colors';

import withStyles from '@mui/styles/withStyles';
import { Button, Grid, Tooltip, Typography } from '@mui/material';
import { Add, ChangeCircle, Delete, Edit, HelpOutline } from '@mui/icons-material';
import AppLogo from '../ui/AppLogo';
import AbEnvironment from '../auth/AbEnvironment';
const abEnvironment = new AbEnvironment();

const styleSheet = theme => ({
  modelLabel: {
    //color: theme.palette.secondary.main,
    fontWeight: 'bold',
  },
  rowButtonEdit: {
    minWidth: 32,
    width: 32,
  },
  rowButtonDelete: {
    minWidth: 32,
    width: 32,
    marginRight: 0,
  },
});

class SubscriptionTableRow extends React.Component {

    render() {
        const classes = this.props.classes;
        const subscription = this.props.subscription;
        const StyledTableRow  = this.props.styledTableRow;
        const StyledTableCell = this.props.styledTableCell;
        const StyledTableCellNarrow = this.props.styledTableCellNarrow;

        // The passed subscription is a customerApp object in normal usage, but
        // an orderLine object for SO history.  In the SO build and accept cases
        // that have a customerApp, we also look at related bcStatus and pwd, which
        // are not present in the orderLine object.  So depending on the
        // available object, we'll derive information needed for display.
        
        let viewContext = 'resellerSetup';
        if (this.props.soContext === 'customerView') {
          viewContext = 'customerView';
        } else if (this.props.soContext === 'orderHistory') {
          viewContext = 'orderHistory';
        }

        let termsChanged = false;
        let newSubscription = false;
        let changedSubscription = false;
        let editableSubscription = false;
        let paidSubscription = false;

        // Available on all objects
        let markupPercent = subscription.appMarkupPercentage;
        let paymentOrder  = subscription.paymentOrder;

        let expensingActive = false;
        let expensing = abEnvironment.getFlagValue("expensing");
        if (expensing != null && expensing.flagValue === 1) {
          expensingActive = true;
        }
      
        //
        // Now handle differences in entry objects
        //
        let editTermsTooltip = 'Edit subscription terms';
        if (subscription.bindCardTByBcId != null) {
          // Payment order - flag as a change if rValue is different from value, but 0 and 1 should be treated as the same.
          if (subscription.rPaymentOrder !== null && subscription.rPaymentOrder !== paymentOrder) {
            if (paymentOrder === 1 && subscription.rPaymentOrder === 0) {
              // treat as the same
            } else {
              termsChanged = true;
            }
            // Use the rValue
            paymentOrder = subscription.rPaymentOrder;
          }

          if (subscription.rAppMarkupPercentage !== null && subscription.rAppMarkupPercentage !== markupPercent) {
            termsChanged = true;
            markupPercent = subscription.rAppMarkupPercentage;
          }

          if (subscription.rPassThroughFees !== null) {
            termsChanged = true;
          }

          if (subscription.bindCardTByBcId.bcStatus === -1 || ((subscription.bindCardTByBcId.bcStatus === 4 || subscription.bindCardTByBcId.bcStatus === 5) && subscription.pwd == null)) {
            newSubscription = true;
          } else if (subscription.bindCardTByBcId.bcStatus !== -1 && termsChanged === true) {
            changedSubscription = true;
          }

          //if (subscription.bindCardTByBcId.bcStatus !== -1 && subscription.bindCardTByBcId.bcStatus !== 4) {
          /*
          if (subscription.bindCardTByBcId.bcStatus !== -1) {
              editableSubscription = true;

              if (subscription.bindCardTByBcId.bcStatus === 4) {
                editTermsTooltip = 'Change customer instructions';
              }
          }
          */
          // Allow editing of pending paid subscriptions.
          //
          editableSubscription = true;
          if (subscription.paymentOrder === 4) {
            editTermsTooltip = 'Change customer instructions';
          } else if (subscription.paymentOrder === 5) {
            editableSubscription = false;
          } else if (subscription.bindCardTByBcId.bcStatus === 4) {
            editTermsTooltip = 'Change customer instructions';
          }

          if ((paymentOrder === 0 || paymentOrder === 1) && subscription.bindCardTByBcId.bcStatus !== 4 && subscription.bindCardTByBcId.bcStatus !== 5) {
            paidSubscription = true;
          }

          // Feature flag check.  If this is an expensed subscription (0, 1, or 2), and the expensing feature flag is not enabled, don't
          // allow editing.
          if (expensingActive === false && (paymentOrder === 0 || paymentOrder === 1 || paymentOrder === 2)) {
            editableSubscription = false;
          }

        } else if (subscription.orderLineId != null) {
          // In the future, we'll have some information on the orderLine object to make the decision.
          newSubscription = false;
          changedSubscription = false;

          if (paymentOrder === 0 || paymentOrder === 1) {
            paidSubscription = true;
          }
        }

        // console.log('paymentOrder is ' + paymentOrder);

        let billingModel = 'Expense to customer';
        let billingModelTooltip = null;
        if (viewContext === 'customerView' || viewContext === 'orderHistory') {
          billingModel = "We'll buy it with your expense wallet";
          billingModelTooltip = "We're setting up and buying this subscription on your behalf, using your expense wallet to pay for the subscription.";
        }
        if (paymentOrder === 2) {
          billingModel = 'I will pay';
          if (viewContext === 'customerView' || viewContext === 'orderHistory') {
            billingModel = "We'll buy it for you";
            billingModelTooltip = "We're setting up and buying this subscription for you using our own payment method.";
          }
        } else if (paymentOrder === 4) {
          billingModel = 'Ask customer for access';
          if (viewContext === 'customerView' || viewContext === 'orderHistory') {
            billingModel = 'We request access';
            billingModelTooltip = "In order to help you manage this subscription, we'll need you to share access with us.  Depending on the subscription, you might be able to invite our team as another user, or you can use our platform to securely share your login and password.";
          }
        } else if (paymentOrder === 5) {
          billingModel = 'I will sign up';
          if (viewContext === 'customerView' || viewContext === 'orderHistory') {
            billingModel = 'We will sign up for you';
            billingModelTooltip = "We'll sign up for you, and set up your subscription.";
          }
        }

        let secondaryText = '';
        if (paidSubscription === true) {
          if (Number(markupPercent) !== 0) {
            if (Number(markupPercent) > 0) {
              if (viewContext === 'customerView' || viewContext === 'orderHistory') {
                secondaryText = markupPercent + '% management fee added';
              } else {
                secondaryText = 'Management fee ' + markupPercent + '%';
              }
           } else {
              let discountPercentage = Number(markupPercent) * -1;
              let discountString = discountPercentage.toLocaleString("en-US", { minimumFractionDigits: 2 });
              if (viewContext === 'customerView' || viewContext === 'orderHistory') {
                secondaryText = discountString + '% discount included';
              } else {
                secondaryText = 'Discount ' + discountPercentage.toLocaleString("en-US", { minimumFractionDigits: 2 }) + '%';
              }
            }
          } else {
            secondaryText = '';
          }
        }

        let pendingTermsTooltip = 'Remove pending subscription';
        const cancelColor = red[300];

        let editable = false;
        if (this.props.editable === true && this.props.confirmDelete !== undefined && this.props.showSubscriptionEdit !== undefined && subscription.bindCardTByBcId != null) {
          editable = true;
        }

        let showModel = false;
        if (this.props.showModel === true) {
            showModel = true;
        }

        let showTerms = true;
        if (this.props.showTerms === false) {
            showTerms = false;
        }

        if (viewContext === 'customerView' || viewContext === 'orderHistory') {
          showModel = false;
          showTerms = false;
        }

        return (
          <StyledTableRow key={subscription.customerAppId}>
            <StyledTableCell style={{width: 24}}>
              {newSubscription === true &&
              <Tooltip placement="right" title="New subscription">
                <Add style={{ paddingTop: 5}}/>
              </Tooltip>}
              {changedSubscription === true &&
              <Tooltip placement="right" title="Terms for this subscription have changed">
                <ChangeCircle style={{ paddingTop: 5}}/>
              </Tooltip>}
            </StyledTableCell>
            <StyledTableCell>
              <Grid container alignItems="center" spacing={1}>
                <Grid item >
                  <AppLogo logoPath={subscription.appTByAppId.appLogo} appName={subscription.appTByAppId.appName} imageHeight='24px'/>
                </Grid>
                <Grid item>
                  <Typography>{subscription.appTByAppId.appName}</Typography>
                </Grid>                            
              </Grid>
            </StyledTableCell>
            {showModel === true &&
            <StyledTableCell>{billingModel}</StyledTableCell>}
            {showTerms === true &&
            <StyledTableCell>{secondaryText}</StyledTableCell>}
            {(viewContext === 'customerView' || viewContext === 'orderHistory') &&
            <StyledTableCell>
              <Grid container spacing={0} alignItems="center">
                <Grid item>
                  <Typography variant="body2" className={classes.modelLabel}>{billingModel}</Typography>
                </Grid>
                <Grid item>
                  <Tooltip title={billingModelTooltip}>
                    <HelpOutline fontSize="small" style={{verticalAlign: "-13%", marginLeft: '4px'}}/>
                  </Tooltip>
                </Grid>
                {secondaryText !== '' &&
                <Grid item xs={12}>
                  <Typography variant="body2">{secondaryText}</Typography>
                </Grid>}
              </Grid>
            </StyledTableCell>}

            {editable === true &&
            <React.Fragment>
              <StyledTableCellNarrow>
                {editableSubscription === true &&
                <Tooltip title={editTermsTooltip}>
                  <Button
                    className={classes.rowButtonEdit}
                    onClick={() => { this.props.showSubscriptionEdit(subscription);}}>
                    <Edit />
                  </Button>
                </Tooltip>}
              </StyledTableCellNarrow>
              <StyledTableCellNarrow>
                {newSubscription === true &&
                <Tooltip title={pendingTermsTooltip}>
                  <Button
                    className={classes.rowButtonDelete}
                    onClick={() => { this.props.confirmDelete(subscription);}}>
                    <Delete htmlColor={cancelColor}/>
                  </Button>
                </Tooltip>}
              </StyledTableCellNarrow>
            </React.Fragment>}

            {editable === false && this.props.soContext === 'resellerSetup' &&
            <React.Fragment>
              <StyledTableCell/>
              <StyledTableCell/>
            </React.Fragment>}
            {editable === false && this.props.soContext !== 'resellerSetup' &&
            <StyledTableCell/>}
          </StyledTableRow>
        );    
    }
}

SubscriptionTableRow.propTypes = {
    subscription:    PropTypes.object.isRequired,
    styledTableRow:  PropTypes.object.isRequired,
    styledTableCell: PropTypes.object.isRequired,
    editable:        PropTypes.bool.isRequired,
    showModel:       PropTypes.bool.isRequired,
};

export default withStyles(styleSheet)(SubscriptionTableRow);;
